import { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Money, StatusPill } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const LABEL = { pending: "To verify", approved: "Approved", rejected: "Rejected" };
const PILL = { pending: "pending", approved: "delivered", rejected: "failed" };

export const UtrClaims = ({ items, onChange }) => {
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const approve = async (c) => {
    try { await api.post(`/payment-claims/${c.id}/approve`); toast.success(`${c.customer_name}: payment recorded`); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async () => {
    try { await api.post(`/payment-claims/${rejecting.id}/reject`, { reason }); toast("Payment marked as not received"); setRejecting(null); setReason(""); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto" data-testid="utr-claims-table">
      <p className="px-2 pb-3 text-sm text-muted-foreground">Check each UTR in your PhonePe business app / bank statement, then approve it to credit the customer's account.</p>
      <Table>
        <TableHeader><TableRow><TableHead>Submitted</TableHead><TableHead>Customer</TableHead><TableHead>UTR</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No UPI payments submitted yet</TableCell></TableRow>}
          {items.map((c) => (
            <TableRow key={c.id} data-testid={`claim-row-${c.id}`}>
              <TableCell className="text-sm">{fmtDateTime(c.created_at)}</TableCell>
              <TableCell><div className="font-semibold">{c.customer_name}</div><div className="text-xs text-muted-foreground">{c.customer_phone}</div></TableCell>
              <TableCell className="font-mono text-sm" data-testid={`claim-utr-${c.id}`}>{c.utr}</TableCell>
              <TableCell className="text-right"><Money value={c.amount} className="font-semibold" /></TableCell>
              <TableCell><StatusPill status={PILL[c.status]} label={LABEL[c.status]} />{c.reviewed_by && <div className="text-xs text-muted-foreground mt-1">{c.reviewed_by}{c.reason ? ` · ${c.reason}` : ""}</div>}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {c.status === "pending" && <>
                  <Button size="sm" className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => approve(c)} data-testid={`claim-approve-${c.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Approve</Button>
                  <Button size="sm" variant="outline" className="ml-2 border-rose-200 text-rose-700" onClick={() => setRejecting(c)} data-testid={`claim-reject-${c.id}`}><XCircle className="mr-1 h-3 w-3" />Reject</Button>
                </>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-sm" data-testid="claim-reject-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject UTR {rejecting?.utr}?</DialogTitle></DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. payment not found)" data-testid="claim-reject-reason" />
          <DialogFooter><Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={reject} data-testid="claim-reject-confirm">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
